import { useEffect, useRef, useState } from "react";
import { C, CSM_LIST } from "../../../../constants";
import { AVATAR_COLORS } from "../../../../constants/colors";
import { daysUntil, fmtMoney, getInit, hoursW, projectPriority, relTime, uid } from "../../../../utils";
import { Chip, PBadge } from "../../../../shared/components/AppUiAtoms";
import apiClient from "../../../../services/api";

// ── Comment Feed (unchanged — local notes) ─────────────────────────────────

function CommentFeed({
  clientName,
  comments,
  setComments,
  addNotification,
  currentUser,
  AvatarComponent,
  MentionInputComponent,
  RenderMentionsComponent,
  parseMentions,
}) {
  const [text, setText] = useState("");
  const [ctype, setCtype] = useState("comment");
  const bottomRef = useRef(null);
  const items = comments[clientName] || [];

  function post() {
    if (!text.trim()) return;
    const mentions = parseMentions(text);
    const newComment = {
      id: uid(),
      author: currentUser.name,
      initials: currentUser.initials,
      text: text.trim(),
      ts: new Date().toISOString(),
      type: ctype,
      mentions,
    };
    setComments((prev) => ({ ...prev, [clientName]: [...(prev[clientName] || []), newComment] }));
    mentions.forEach((mention) => {
      if (mention.toLowerCase() !== currentUser.name.toLowerCase()) {
        addNotification?.({ id: uid(), type: "mention", text: `You were @mentioned in a note on "${clientName}"`, ts: new Date().toISOString(), read: false });
      }
    });
    setText("");
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  return (
    <div className="feed">
      <div className="feed-head"><span className="feed-title">Team Notes</span><span className="feed-cnt">{items.length}</span></div>
      <div className="feed-body">
        {items.length === 0 && <div style={{ textAlign: "center", padding: "28px 0", color: C.dim, fontSize: 12 }}>No notes yet.</div>}
        {items.map((comment) => (
          <div className="cmt" key={comment.id}>
            <AvatarComponent initials={comment.initials || getInit(comment.author)} size={28} />
            <div className="cmt-body">
              <div className="cmt-hdr">
                <span className="cmt-author">{comment.author}</span>
                {comment.type === "update" && <span className="cmt-upd">Update</span>}
                <span className="cmt-ts">{relTime(comment.ts)}</span>
              </div>
              <div className="cmt-text"><RenderMentionsComponent text={comment.text} /></div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="feed-foot">
        <div className="compose">
          <AvatarComponent initials={currentUser.initials} size={26} />
          <div className="compose-area">
            <MentionInputComponent value={text} onChange={setText} placeholder="Add a note… type @name to mention a colleague" rows={3} onSubmit={post} />
            <div className="ctype-row"><div className="ctype-btns">{["comment", "update"].map((type) => <button key={type} className={`ctype-btn${ctype === type ? " sel" : ""}`} onClick={() => setCtype(type)}>{type.charAt(0).toUpperCase() + type.slice(1)}</button>)}</div><button className="post-btn" onClick={post} disabled={!text.trim()}>Post</button></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// Resolve the effective NPS for display/priority: per-project override
// first, then client-level fallback, then undefined when neither is set.
// 0 is treated as "never set" because (a) before Phase B, Project.NPS
// had @default(0) so many rows carry 0 meaning "unset", and (b) on the
// 1-5 Monday WBR scale, 0 isn't a meaningful rating anyway.
function effectiveNps(project, clientNps) {
  if (typeof project?.NPS === "number" && project.NPS > 0) return project.NPS;
  if (typeof project?.nps === "number" && project.nps > 0) return project.nps;
  if (typeof clientNps === "number" && clientNps > 0) return clientNps;
  return undefined;
}

// Normalise one of our project shapes (API result OR legacy prop) into the
// fields projectPriority() expects.
function normaliseProject(p, clientNps) {
  const status = p.projectStatus || p.status || p.trackerStatus || "";
  return {
    projectStatus: /critical/i.test(status) ? "Critical" : /at.?risk/i.test(status) ? "At Risk" : status,
    NPS: effectiveNps(p, clientNps),
    contractEndDate: p.contractEndDate,
    weeklyReportStatus: p.weeklyReportStatus,
    profitPct: p.profitPct ?? 0,
    utilizationPct: p.utilizationPct ?? 0,
  };
}

// Worst project status for the card "Risk" chip. Mirrors old-design logic.
function worstProjectStatus(projects) {
  const statuses = (projects || []).map((p) => p.status || p.projectStatus || p.trackerStatus || "");
  if (statuses.some((s) => /critical/i.test(s))) return "Critical";
  if (statuses.some((s) => /at.?risk/i.test(s))) return "At Risk";
  return "On Track";
}

// Top (worst) priority score across a client's projects. clientNps is the
// Client-level fallback used when a project has no explicit NPS override.
function topPriorityScore(projects, clientNps) {
  return (projects || []).reduce((m, p) => Math.max(m, projectPriority(normaliseProject(p, clientNps))), 0);
}

// (avgEffectiveNps helper intentionally removed: Client.nps is the single
//  source of truth at the client level. Per-project NPS overrides are
//  displayed per-project only and don't aggregate up — that was the
//  confusion pattern reported in the UI.)

// Small avatar circle used in the assigned-consultants pills.
function AvatarDot({ name, size = 20 }) {
  const initials = getInit(name);
  const bg = AVATAR_COLORS[initials] || "#3b7ffa";
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size <= 20 ? 8 : 10, fontWeight: 700, color: "#fff",
    }}>{initials}</div>
  );
}

// ── Client List Page ───────────────────────────────────────────────────────

export function ClientListPage({ projects, clients, comments, nav, showToast, KpiCardComponent, PriorityBadgeComponent }) {
  const [search, setSearch] = useState("");
  const [prioF, setPrioF] = useState("All");
  const [syncing, setSyncing] = useState(false);
  const [enrichedClients, setEnrichedClients] = useState(null);

  useEffect(() => {
    apiClient.getClientOverview().then(setEnrichedClients).catch(() => {});
  }, []);

  async function handleSync() {
    setSyncing(true);
    try {
      const result = await apiClient.triggerFullSync();
      showToast?.(`Synced ${result.clientResult?.created || 0} clients, ${result.projectResult?.created || 0} projects`, "success");
      const fresh = await apiClient.getClientOverview();
      setEnrichedClients(fresh);
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Sync failed", "error");
    } finally { setSyncing(false); }
  }

  // Use enriched data when available; fall back to legacy props so the page
  // still renders before the enrichment API call resolves.
  const displayClients = enrichedClients || clients.map((c) => ({
    id: c.clientId || c.id,
    name: c.clientName || c.name,
    csm: c.csm,
    owner: null,
    priority: c.priority || "STANDARD",
    nps: null,
    totalRevenue: 0,
    totalHours: 0,
    activeProjectCount: 0,
    noteCount: (comments[c.clientName || c.name] || []).length,
    projects: projects
      .filter((p) => p.clientName === (c.clientName || c.name))
      .map((p) => ({
        projectId: p.projectId || p.id,
        name: p.projectName || p.name,
        status: p.projectStatus || p.status,
        NPS: p.NPS,
        contractEndDate: p.contractEndDate,
        weeklyReportStatus: p.weeklyReportStatus,
        profitPct: p.profitPct,
        utilizationPct: p.utilizationPct,
      })),
  }));

  // Attach derived priority + risk so we can sort + filter + render off one
  // shape. "Avg NPS" on the card is Client.nps directly — the single
  // source of truth edited from Client Detail. Per-project overrides are
  // visible inside Client Detail's project cards, not rolled up here.
  const withStats = displayClients.map((c) => {
    const prioScore = topPriorityScore(c.projects, c.nps);
    return {
      ...c,
      prioScore,
      prioLabel: prioScore >= 55 ? "P1" : prioScore >= 28 ? "P2" : "P3",
    };
  });

  const filtered = withStats
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    .filter((c) => prioF === "All" || c.prioLabel === prioF)
    .sort((a, b) => b.prioScore - a.prioScore || (b.totalRevenue || 0) - (a.totalRevenue || 0));

  return (
    <div className="fade">
      <div className="pg-top">
        <div className="pg-title">Client Overview</div>
        <div className="pg-sub">Consolidated view across all client accounts{enrichedClients ? ` · ${enrichedClients.length} synced from tracker` : ""}</div>
      </div>
      <div className="pg-body">
        <div className="fbar">
          <input className="finput" placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
          <select className="fsel" value={prioF} onChange={(e) => setPrioF(e.target.value)}>
            {["All", "P1", "P2", "P3"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="finfo"><strong>{filtered.length}</strong> client{filtered.length !== 1 ? "s" : ""}</div>
          <button className="btn-add" style={{ marginLeft: "auto" }} onClick={handleSync} disabled={syncing} title="Re-sync from tracker + Monday">
            {syncing ? "Syncing…" : "↻ Sync All"}
          </button>
          <button className="btn-add" onClick={() => nav("new-client")}>+ Add Client</button>
        </div>

        <div className="cl-grid">
          {filtered.map((c) => {
            const projectCount = c.activeProjectCount || c.projects?.length || 0;
            const noteCount = c.noteCount ?? (comments[c.name] || []).length;
            return (
              <div
                key={c.id}
                className={`clc${c.prioLabel === "P1" ? " p1" : c.prioLabel === "P2" ? " p2" : ""}`}
                onClick={() => nav("client", c.id)}
              >
                <div className="clc-prio"><PBadge score={c.prioScore} /></div>
                <div className="clc-name">{c.name}</div>
                <div className="clc-meta">{c.owner || c.csm || "Unassigned"} · {projectCount} project{projectCount !== 1 ? "s" : ""}</div>
                <div className="clc-stats">
                  <div className="clc-stat">
                    <div className="clc-stat-lbl">Revenue</div>
                    <div className="clc-stat-val" style={{ color: C.blue }}>{fmtMoney(c.totalRevenue || 0)}</div>
                  </div>
                  <div className="clc-stat">
                    <div className="clc-stat-lbl">Hours</div>
                    <div className="clc-stat-val">{Math.round(c.totalHours || 0).toLocaleString()}h</div>
                  </div>
                  <div className="clc-stat">
                    <div className="clc-stat-lbl">Client NPS</div>
                    <div
                      className="clc-stat-val"
                      style={{
                        color: typeof c.nps === "number"
                          ? (c.nps >= 4 ? C.green : c.nps >= 3 ? C.yellow : C.red)
                          : C.dim,
                      }}
                    >{c.nps ?? "—"}</div>
                  </div>
                  <div className="clc-stat">
                    <div className="clc-stat-lbl">Risk</div>
                    <div style={{ marginTop: 4 }}><Chip status={worstProjectStatus(c.projects)} /></div>
                  </div>
                </div>
                <div className="clc-projs">
                  {(c.projects || []).map((p) => (
                    <div className="clc-prow" key={p.projectId || p.name}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{p.name}</span>
                      <Chip status={p.status || "On Track"} />
                    </div>
                  ))}
                </div>
                <div className="clc-foot">
                  <span style={{ fontSize: 11, color: C.dim }}>💬 {noteCount} note{noteCount !== 1 ? "s" : ""}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Client Detail Page ─────────────────────────────────────────────────────

export function ClientDetailPage({ clientId, projects, comments, setComments, addNotification, nav, showToast, currentUser, AvatarComponent, ChipComponent, PriorityBadgeComponent, CommentAtoms }) {
  const [detail, setDetail] = useState(null);
  const [editingNps, setEditingNps] = useState(false);
  const [npsDraft, setNpsDraft] = useState("");
  const [editingDetails, setEditingDetails] = useState(false);

  // Tracker-critical client fields that must stay non-empty once set on
  // manual edit. Mirrors the Project-side MANDATORY_FIELDS pattern and the
  // CreateForms.jsx CL_SCHEMA so the full create/edit surface enforces the
  // same contract.
  const CLIENT_MANDATORY = [
    { key: "name", label: "Name" },
    { key: "csm", label: "CSM" },
    { key: "contactPerson", label: "Primary Contact" },
    { key: "email", label: "Contact Email" },
    { key: "priority", label: "Priority" },
  ];
  const clientIsBlank = (v) => v === undefined || v === null || v === "";
  const missingClientFields = detail
    ? CLIENT_MANDATORY.filter((f) => clientIsBlank(detail[f.key]))
    : [];

  async function saveClientField(field, value) {
    const rule = CLIENT_MANDATORY.find((f) => f.key === field);
    if (rule && clientIsBlank(value)) {
      showToast?.(`${rule.label} is required — cannot be cleared`, "error");
      return;
    }
    if (field === "email" && value && !/.+@.+\..+/.test(String(value))) {
      showToast?.("Email format invalid", "error");
      return;
    }
    try {
      const updated = await apiClient.updateClient(detail?.id || clientId, { [field]: value });
      setDetail((d) => (d ? { ...d, ...updated } : d));
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Update failed", "error");
    }
  }

  useEffect(() => {
    if (!clientId) return;
    apiClient.getClientDetail(clientId).then(setDetail).catch(() => {});
  }, [clientId]);

  // Save Client.nps — the single source of truth that drives Financial Status,
  // Client Overview Avg NPS, and the per-project fallback in Delivery.
  // Blank input clears to null (no client-level NPS).
  async function saveClientNps(raw) {
    const trimmed = String(raw).trim();
    const numValue = trimmed === "" ? null : Number(trimmed);
    if (numValue !== null && (Number.isNaN(numValue) || numValue < 0 || numValue > 5)) {
      showToast?.("NPS must be a number between 0 and 5", "error");
      return;
    }
    try {
      const updated = await apiClient.updateClient(detail?.id || clientId, { nps: numValue });
      setDetail((d) => (d ? { ...d, nps: updated.nps } : d));
      setEditingNps(false);
      showToast?.("Client NPS updated", "success");
    } catch (err) {
      showToast?.(err.response?.data?.error || err.message || "Update failed", "error");
    }
  }

  // Legacy fallback: when navigated by clientName (old callers) and the API
  // call hasn't returned yet, render from the props data.
  const legacyClient = !detail && clientId ? projects.find((p) => p.clientName === clientId || p.clientId === clientId) : null;
  const clientName = detail?.name || legacyClient?.clientName || clientId;
  const clientProjects = detail?.projects || projects.filter((p) => p.clientName === clientName);

  // Totals + averages. Prefer the API's pre-computed aggregates when present.
  const totalRevenue = detail?.totalRevenue ?? clientProjects.reduce((s, p) => s + (p.billables || 0), 0);
  const totalHours = detail?.totalHours ?? clientProjects.reduce((s, p) => {
    const hrs = p.loggedHours ?? (p.ratePerHour ? hoursW(p.billables || 0, p.ratePerHour) : 0);
    return s + hrs;
  }, 0);
  // Client NPS is the single source of truth shown at the client level.
  // Per-project overrides live inside each project card below, not here,
  // so users never see two conflicting "this client's NPS" numbers.
  const clientLevelNps = detail?.nps;
  const avgUtil = detail?.avgUtil ?? (clientProjects.length > 0
    ? Math.round(clientProjects.reduce((s, p) => s + (p.utilizationPct || 0), 0) / clientProjects.length)
    : 0);
  const avgProfit = detail?.avgProfit ?? (clientProjects.length > 0
    ? Math.round(clientProjects.reduce((s, p) => s + (p.profitPct || 0), 0) / clientProjects.length)
    : 0);

  const worstStatus = worstProjectStatus(clientProjects);
  const topPrio = topPriorityScore(clientProjects, clientLevelNps);
  const lastCheckin = clientProjects
    .map((p) => p.lastClientCheckin)
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || (detail?.lastSyncedAt ? new Date(detail.lastSyncedAt).toISOString().slice(0, 10) : null);

  return (
    <div className="fade det">
      <div className="det-back" onClick={() => nav("clients")}>← Clients</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <div className="det-title">{clientName}</div>
        <Chip status={worstStatus} />
        <PBadge score={topPrio} />
      </div>
      <div className="det-sub">
        {clientProjects.length} active project{clientProjects.length !== 1 ? "s" : ""}
        {lastCheckin && ` · Last check-in: ${lastCheckin}`}
      </div>

      {/* Client NPS — single source of truth for Financial Status and the
          Client Overview card. Per-project overrides live in Delivery. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 18px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em" }}>Client NPS</span>
        {editingNps ? (
          <>
            <input
              type="number" min="0" max="5" step="0.1" autoFocus
              value={npsDraft}
              onChange={(e) => setNpsDraft(e.target.value)}
              onBlur={() => saveClientNps(npsDraft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveClientNps(npsDraft);
                if (e.key === "Escape") { setEditingNps(false); }
              }}
              style={{
                width: 80, padding: "4px 8px", borderRadius: 6,
                background: "#0a1528", border: "1px solid #14213d", color: C.text,
                fontFamily: "'Noto Sans Mono',monospace", fontSize: 13,
              }}
            />
            <span style={{ fontSize: 11, color: C.dim }}>/ 5 · Enter to save, Esc to cancel</span>
          </>
        ) : (
          <>
            <span
              className="mono"
              style={{
                fontSize: 16, fontWeight: 700, cursor: "pointer",
                color: typeof detail?.nps === "number"
                  ? (detail.nps >= 4 ? C.green : detail.nps >= 3 ? C.yellow : C.red)
                  : C.dim,
              }}
              onClick={() => { setNpsDraft(detail?.nps != null ? String(detail.nps) : ""); setEditingNps(true); }}
              title="Click to edit"
            >
              {typeof detail?.nps === "number" ? `${detail.nps} / 5` : "— Not set"}
            </span>
            <button
              type="button"
              onClick={() => { setNpsDraft(detail?.nps != null ? String(detail.nps) : ""); setEditingNps(true); }}
              style={{
                fontSize: 11, padding: "2px 10px", borderRadius: 5,
                background: "transparent", color: C.sub, border: `1px solid ${C.dim}33`, cursor: "pointer",
              }}
            >✎ Edit</button>
            <span style={{ fontSize: 11, color: C.dim }}>(drives Financial Status + Overview)</span>
          </>
        )}
      </div>

      {missingClientFields.length > 0 && (
        <div style={{
          margin: "4px 0 14px", padding: "10px 14px", borderRadius: 8,
          background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.35)",
          fontSize: 12, color: "#f59e0b", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <span style={{ fontWeight: 700 }}>⚠ Client record incomplete.</span>
          <span>Missing required fields:</span>
          <span style={{ fontWeight: 600 }}>{missingClientFields.map((f) => f.label).join(" · ")}</span>
        </div>
      )}
      <div className="icard" style={{ marginBottom: 16 }}>
        <div className="icard-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Client Details</span>
          <button
            type="button"
            onClick={() => setEditingDetails((e) => !e)}
            style={{ background: "none", border: "1px solid #14213d", borderRadius: 5, padding: "2px 10px", color: C.blue, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
          >{editingDetails ? "Done" : "✎ Edit"}</button>
        </div>
        {editingDetails ? (
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 8, columnGap: 10, fontSize: 12 }}>
            <span style={{ color: C.dim }}>CSM</span>
            <select
              defaultValue={detail?.csm || ""}
              onChange={(e) => saveClientField("csm", e.target.value)}
              style={{ background: "#0a1528", border: "1px solid #14213d", color: C.text, borderRadius: 5, padding: "4px 8px", fontSize: 12 }}
            >
              <option value="">— Select CSM —</option>
              {CSM_LIST.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <span style={{ color: C.dim }}>Primary Contact</span>
            <input
              type="text"
              defaultValue={detail?.contactPerson || ""}
              onBlur={(e) => { if (e.target.value !== (detail?.contactPerson || "")) saveClientField("contactPerson", e.target.value); }}
              style={{ background: "#0a1528", border: "1px solid #14213d", color: C.text, borderRadius: 5, padding: "4px 8px", fontSize: 12 }}
            />
            <span style={{ color: C.dim }}>Contact Email</span>
            <input
              type="email"
              defaultValue={detail?.email || ""}
              onBlur={(e) => { if (e.target.value !== (detail?.email || "")) saveClientField("email", e.target.value); }}
              style={{ background: "#0a1528", border: "1px solid #14213d", color: C.text, borderRadius: 5, padding: "4px 8px", fontSize: 12 }}
            />
            <span style={{ color: C.dim }}>Phone</span>
            <input
              type="tel"
              defaultValue={detail?.phone || ""}
              onBlur={(e) => { if (e.target.value !== (detail?.phone || "")) saveClientField("phone", e.target.value); }}
              style={{ background: "#0a1528", border: "1px solid #14213d", color: C.text, borderRadius: 5, padding: "4px 8px", fontSize: 12 }}
            />
            <span style={{ color: C.dim }}>Priority</span>
            <select
              defaultValue={detail?.priority || "STANDARD"}
              onChange={(e) => saveClientField("priority", e.target.value)}
              style={{ background: "#0a1528", border: "1px solid #14213d", color: C.text, borderRadius: 5, padding: "4px 8px", fontSize: 12 }}
            >
              <option value="STANDARD">Standard</option>
              <option value="STRATEGIC">Strategic</option>
              <option value="VIP">VIP</option>
              <option value="ESCALATED">Escalated</option>
            </select>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 6, fontSize: 12 }}>
            <span style={{ color: C.dim }}>CSM</span><span>{detail?.csm || "—"}</span>
            <span style={{ color: C.dim }}>Primary Contact</span><span>{detail?.contactPerson || "—"}</span>
            <span style={{ color: C.dim }}>Contact Email</span><span>{detail?.email || "—"}</span>
            <span style={{ color: C.dim }}>Phone</span><span>{detail?.phone || "—"}</span>
            <span style={{ color: C.dim }}>Priority</span><span>{detail?.priority || "STANDARD"}</span>
          </div>
        )}
      </div>
      {/* 5-KPI stat strip, matches old-design .tot-bar */}
      <div className="tot-bar">
        {[
          ["Total Revenue", fmtMoney(totalRevenue), C.blue],
          ["Total Hours", `${Math.round(totalHours).toLocaleString()}h`, C.text],
          ["Client NPS", typeof clientLevelNps === "number" ? `${clientLevelNps} / 5` : "—", typeof clientLevelNps === "number" ? (clientLevelNps >= 4 ? C.green : clientLevelNps >= 3 ? C.yellow : C.red) : C.dim],
          ["Avg Util", `${avgUtil}%`, avgUtil >= 80 ? C.green : avgUtil >= 60 ? C.yellow : C.red],
          ["Avg Profit", `${avgProfit}%`, avgProfit >= 30 ? C.green : avgProfit >= 15 ? C.yellow : C.red],
        ].map(([label, value, col]) => (
          <div className="tot-item" key={label}>
            <div className="tot-lbl">{label}</div>
            <div className="tot-val" style={{ color: col }}>{value}</div>
          </div>
        ))}
      </div>

      <div className="cl-det">
        <div className="pb">
          {clientProjects.length === 0 && <div style={{ fontSize: 12, color: C.dim, padding: 12 }}>No projects yet.</div>}
          {clientProjects.map((p) => {
            const projectId = p.id || p.projectId;
            const name = p.name || p.projectName;
            const status = p.projectStatus || p.status || p.trackerStatus;
            const billables = p.billables || 0;
            const rate = p.ratePerHour || 0;
            const hrs = p.loggedHours ?? (rate ? hoursW(billables, rate) : 0);
            // Per-project override first, Client-level fallback second.
            const resolvedNps = effectiveNps(p, clientLevelNps);
            const nps = typeof resolvedNps === "number" ? resolvedNps : 0;
            const profit = p.profitPct ?? 0;
            const util = p.utilizationPct ?? 0;
            const dEnd = p.contractEndDate ? daysUntil(p.contractEndDate) : null;
            const prioScore = projectPriority(normaliseProject(p, clientLevelNps));
            const assigned = p.assignedConsultants || [];
            return (
              <div className="proj-c" key={projectId || name}>
                <div className="proj-c-head" onClick={() => projectId && nav("project", projectId)}>
                  <div>
                    <div className="proj-c-name">{name}</div>
                    <div className="proj-c-sub">
                      {p.billingModel || "—"}
                      {p.projectOwner ? ` · ${p.projectOwner}` : ""}
                      {p.contractEndDate ? ` · ends ${String(p.contractEndDate).slice(0, 10)}` : ""}
                      {dEnd !== null && dEnd < 45 ? ` (${dEnd}d)` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 7, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {p.weeklyReportStatus && <Chip status={p.weeklyReportStatus} />}
                    <Chip status={status || "On Track"} />
                    <PBadge score={prioScore} />
                  </div>
                </div>
                <div className="proj-c-body">
                  <div className="pstats">
                    {[
                      ["Revenue", fmtMoney(billables), C.blue],
                      ["Hours", `${Math.round(hrs)}h`, C.text],
                      ["NPS", nps || "—", nps >= 4 ? C.green : nps >= 3 ? C.yellow : nps ? C.red : C.dim],
                      ["Profit", `${profit}%`, profit >= 30 ? C.green : profit >= 15 ? C.yellow : C.red],
                    ].map(([label, value, col]) => (
                      <div className="pstat" key={label}>
                        <div className="pstat-lbl">{label}</div>
                        <div className="pstat-val" style={{ color: col }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 6 }}>Assigned</div>
                    {assigned.length > 0 ? (
                      <div className="rpills">
                        {assigned.map((name) => (
                          <div className="rpill" key={name}>
                            <AvatarDot name={name} />
                            {name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>Unassigned</div>
                    )}
                  </div>

                  {p.riskNotes && (
                    <div style={{
                      background: "rgba(245,158,11,.07)", border: "1px solid rgba(245,158,11,.18)",
                      borderRadius: 7, padding: "8px 11px", fontSize: 12, color: C.yellow, lineHeight: 1.6,
                    }}>⚠ {p.riskNotes}</div>
                  )}

                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 9 }}>
                    <div style={{ fontSize: 10, color: C.dim, minWidth: 60 }}>Utilisation</div>
                    <div style={{ flex: 1, background: "#14213d", borderRadius: 3, height: 4 }}>
                      <div style={{
                        width: `${Math.min(100, Math.max(0, util))}%`, height: "100%", borderRadius: 3,
                        background: util >= 80 ? C.green : util >= 60 ? C.yellow : C.red,
                      }} />
                    </div>
                    <span className="mono" style={{ fontSize: 11, color: C.text }}>{util}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <CommentFeed
          clientName={clientName}
          comments={comments}
          setComments={setComments}
          addNotification={addNotification}
          currentUser={currentUser}
          AvatarComponent={AvatarComponent}
          MentionInputComponent={CommentAtoms.MentionInput}
          RenderMentionsComponent={CommentAtoms.RenderMentions}
          parseMentions={CommentAtoms.parseMentions}
        />
      </div>
    </div>
  );
}
